import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Box,
  Paper,
  Avatar,
  TextField,
  Button,
  IconButton,
  Chip,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Fade,
} from '@mui/material';
import moment from 'moment';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { toast } from 'react-hot-toast';
import { isToday, isYesterday, isThisWeek, isThisMonth, parseISO } from 'date-fns';

import { reportsApi } from 'src/api/reportsApi';
import useFormatDate from 'src/hooks/useFormatDate';
import useUsers from 'src/hooks/useUsers';
import { useExpertManagement } from 'src/hooks/useExpertManagement';

const TWO_MINUTES = 2 * 60 * 1000;

const NotesSection = ({ companyName, startDate, endDate, allReports, allReportsMutate, decodedToken, isReportsLoading = false }) => {
  const { users } = useUsers();
  const { experts } = useExpertManagement();

  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showReturnHint, setShowReturnHint] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);

  // Memoize the dates array to prevent infinite re-renders
  const submitDates = useMemo(() => notes.map((note) => note.submitDate), [notes]);
  const formattedDates = useFormatDate(submitDates);

  // Fetch notes for the current report
  const fetchNotes = useCallback(
    async (reportId) => {
      if (!reportId || !decodedToken) return;

      try {
        const response = await reportsApi.getNotes(reportId);
        if (response && Array.isArray(response)) {
          // Parse notes that have JSON content
          const parsedNotes = response.map((note) => {
            try {
              const parsed = JSON.parse(note.content);
              if (parsed.userID && parsed.userType !== undefined && parsed.content) {
                return {
                  id: note.id,
                  reportID: note.reportID,
                  userID: parsed.userID,
                  userType: parsed.userType,
                  content: parsed.content,
                  submitDate: note.submitDate,
                  valid: note.valid,
                };
              }
            } catch (e) {
              // If parsing fails, treat as regular note
              return {
                id: note.id,
                reportID: note.reportID,
                userID: decodedToken?.userID || 0,
                userType: decodedToken?.userType || 0,
                content: note.content,
                submitDate: note.submitDate,
                valid: note.valid,
              };
            }
            return note;
          });

          setNotes(parsedNotes.sort((a, b) => parseISO(b.submitDate) - parseISO(a.submitDate)));
        } else {
          setNotes([]);
        }
      } catch (error) {
        setNotes([]);
      }
    },
    [decodedToken]
  );

  // Find or create report — wait until reports have actually loaded to avoid creating duplicates
  useEffect(() => {
    const findOrCreateReport = async () => {
      if (!allReports || !startDate || !endDate || !decodedToken || isReportsLoading) return;

      // Handle both possible data structures: direct array or object with data property
      const reportsArray = Array.isArray(allReports) ? allReports : allReports?.data || [];

      if (!reportsArray || !Array.isArray(reportsArray)) {
        return;
      }

      // Look for existing report - try multiple company name formats
      const companyVariants = [
        companyName, // Original format
        companyName.replace(/\s+/g, ''), // Remove all spaces
        companyName.toLowerCase(), // lowercase
        companyName.toUpperCase(), // uppercase
      ];

      const existingReport = reportsArray.find((report) => {
        const reportCompanyVariants = [
          report.company,
          report.company.replace(/\s+/g, ''),
          report.company.toLowerCase(),
          report.company.toUpperCase(),
        ];

        const companyMatches =
          companyVariants.some((variant) => reportCompanyVariants.includes(variant)) ||
          reportCompanyVariants.some((variant) => companyVariants.includes(variant));

        const dateMatches = moment(report.startDate).format('YYYY-MM-DD') === startDate && moment(report.endDate).format('YYYY-MM-DD') === endDate;

        return companyMatches && dateMatches;
      });

      if (existingReport) {
        setCurrentReport(existingReport);
        await fetchNotes(existingReport.id);
      } else {
        // Create new report
        try {
          const response = await reportsApi.create(companyName, startDate, endDate);
          if (response) {
            const newReport = {
              id: response,
              company: companyName,
              startDate,
              endDate,
            };
            setCurrentReport(newReport);
            await fetchNotes(response);
          }
        } catch (error) {
          // Error handled silently
        }
      }
    };

    findOrCreateReport();
  }, [allReports, companyName, startDate, endDate, decodedToken, fetchNotes, isReportsLoading]);

  // Notes functionality
  const handleAddNote = async () => {
    if (!newNote.trim() || !currentReport?.id || !decodedToken) return;

    try {
      await reportsApi.addNote(currentReport.id, decodedToken.userID, decodedToken.userType, newNote.trim());
      setNewNote('');
      fetchNotes(currentReport.id);
      toast.success('Note added successfully');
    } catch (error) {
      toast.error('Error adding note');
    }
  };

  const handleEditNote = async () => {
    if (!editingNote || !editingNote.content.trim() || !decodedToken) return;

    try {
      await reportsApi.editNote(editingNote.id, decodedToken.userID, decodedToken.userType, editingNote.content.trim());
      setEditingNote(null);
      fetchNotes(currentReport.id);
      toast.success('Note updated successfully');
    } catch (error) {
      toast.error('Error editing note');
    }
  };

  const handleDeleteClick = (note) => {
    setSelectedNote(note);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedNote?.id) return;
    try {
      await reportsApi.deleteNote(selectedNote.id);
      setDeleteDialogOpen(false);
      setSelectedNote(null);
      fetchNotes(currentReport.id);
      toast.success('Note deleted successfully');
    } catch (error) {
      toast.error('Error deleting note');
    }
  };

  const handleStartEdit = (note) => {
    setEditingNote(note);
  };

  const getUserInfo = (userId, userType) => {
    if (userType === 1) {
      // Look up in experts array
      const expertUser = experts?.find((e) => e.id === userId);
      return {
        name: expertUser ? expertUser.name : 'Unknown Expert',
        avatar: expertUser?.imageURL || '',
        initials: expertUser
          ? expertUser.name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .substring(0, 2)
          : 'E',
      };
    } else {
      // Look up in users array (default behavior)
      const user = users?.find((u) => u.id === userId);
      return {
        name: user ? `${user.firstName} ${user.lastName}` : 'Unknown User',
        avatar: user?.avatar || '',
        initials: user ? `${user.firstName[0]}${user.lastName[0]}` : 'U',
      };
    }
  };

  const handleKeyDown = (event) => {
    // Show return hint when user types
    setShowReturnHint(event.target.value.length > 0);

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (newNote.trim()) {
        handleAddNote();
      }
    } else if (event.key === 'Escape' && editingNote) {
      setEditingNote(null);
    }
  };

  const groupNotesByDate = (notesList) => {
    return notesList.reduce((groups, note) => {
      const date = parseISO(note.submitDate);
      let group;

      if (isToday(date)) group = 'Today';
      else if (isYesterday(date)) group = 'Yesterday';
      else if (isThisWeek(date)) group = 'This Week';
      else if (isThisMonth(date)) group = 'This Month';
      else group = 'Earlier';

      if (!groups[group]) groups[group] = [];
      groups[group].push(note);
      return groups;
    }, {});
  };

  const groupedNotes = groupNotesByDate(notes);
  const orderedGroups = ['Today', 'Yesterday', 'This Week', 'This Month', 'Earlier'];

  return (
    <Box sx={{ mt: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #ddd',
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
            pb: 2,
            mb: 0,
          }}
        >
          <TextField
            fullWidth
            placeholder="Write a note..."
            variant="outlined"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover, &.Mui-focused': {
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
              },
              '& .MuiOutlinedInput-input': {
                p: 1.25,
                fontSize: '0.875rem',
                '&::placeholder': {
                  opacity: 0.8,
                },
                whiteSpace: 'pre-wrap',
              },
            }}
            multiline
            minRows={1}
            maxRows={4}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 1, color: 'text.secondary' }}>
                  <ChatBubbleOutlineIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Fade in={showReturnHint}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, color: 'text.secondary' }}>
                      <KeyboardReturnIcon fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption">Send</Typography>
                    </Box>
                  </Fade>
                  <Button
                    variant="contained"
                    onClick={handleAddNote}
                    disabled={!newNote.trim()}
                    size="small"
                    sx={{
                      minWidth: 'unset',
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      mr: 0.5,
                      opacity: newNote.trim() ? 1 : 0.5,
                      transition: 'opacity 0.2s ease-in-out',
                    }}
                  >
                    <SendIcon fontSize="small" />
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {notes.length > 0 && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
              {orderedGroups.map((group) => {
                if (!groupedNotes[group]?.length) return null;

                return (
                  <Box key={group} sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'text.primary',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 2,
                      }}
                    >
                      {group}
                    </Typography>
                    {groupedNotes[group].map((note, index) => {
                      const userInfo = getUserInfo(note.userID, note.userType);
                      const prevNote = index > 0 ? groupedNotes[group][index - 1] : null;
                      const nextNote = index < groupedNotes[group].length - 1 ? groupedNotes[group][index + 1] : null;

                      // Check if messages are from different users or have a time gap
                      const timeDiffWithPrev = prevNote ? parseISO(prevNote.submitDate).getTime() - parseISO(note.submitDate).getTime() : TWO_MINUTES;
                      const timeDiffWithNext = nextNote ? parseISO(note.submitDate).getTime() - parseISO(nextNote.submitDate).getTime() : TWO_MINUTES;

                      const isFirstInSequence = !prevNote || prevNote.userID !== note.userID || timeDiffWithPrev >= TWO_MINUTES;
                      const isLastInSequence = !nextNote || nextNote.userID !== note.userID || timeDiffWithNext >= TWO_MINUTES;

                      return (
                        <Box
                          key={note.id}
                          sx={{
                            mb: isLastInSequence ? 2 : 0.75,
                            position: 'relative',
                            '&:hover .message-actions': {
                              opacity: 1,
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Box sx={{ width: 32, alignSelf: isFirstInSequence ? 'flex-start' : 'flex-start', mt: isFirstInSequence ? 0.5 : 0 }}>
                              {isFirstInSequence && (
                                <Avatar
                                  src={userInfo.avatar}
                                  alt={userInfo.name}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: 'primary.main',
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {userInfo.initials}
                                </Avatar>
                              )}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                {editingNote?.id === note.id ? (
                                  <Box sx={{ flex: 1 }}>
                                    <TextField
                                      fullWidth
                                      multiline
                                      minRows={2}
                                      maxRows={6}
                                      value={editingNote.content}
                                      onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
                                      onKeyDown={(e) => e.key === 'Escape' && setEditingNote(null)}
                                      variant="outlined"
                                      size="small"
                                      autoFocus
                                      sx={{
                                        bgcolor: 'white',
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 1.5,
                                          fontSize: '0.875rem',
                                        },
                                        '& .MuiOutlinedInput-input': {
                                          whiteSpace: 'pre-wrap',
                                        },
                                      }}
                                    />
                                    <Box sx={{ mt: 0.5, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                      <Button size="small" onClick={() => setEditingNote(null)} sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>
                                        Cancel
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleEditNote}
                                        sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
                                      >
                                        Save
                                      </Button>
                                    </Box>
                                  </Box>
                                ) : (
                                  <>
                                    <Typography
                                      variant="body2"
                                      component="pre"
                                      sx={{
                                        flex: 1,
                                        p: 1.5,
                                        bgcolor: note.userID === decodedToken?.userID ? '#409dff' : 'grey.100',
                                        color: note.userID === decodedToken?.userID ? 'white' : 'text.primary',
                                        borderRadius: 2,
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'inherit',
                                        fontSize: '0.875rem',
                                        lineHeight: 1.5,
                                        maxWidth: 'calc(100% - 40px)',
                                        '& .MuiChip-root': {
                                          bgcolor: note.userID === decodedToken?.userID ? 'rgba(255,255,255,0.2)' : undefined,
                                          color: note.userID === decodedToken?.userID ? 'white' : undefined,
                                        },
                                      }}
                                    >
                                      {isFirstInSequence && (
                                        <Box
                                          component="span"
                                          sx={{
                                            fontWeight: 600,
                                            display: 'block',
                                            mb: 0.5,
                                            color: note.userID === decodedToken?.userID ? 'inherit' : 'text.primary',
                                          }}
                                        >
                                          {userInfo.name}
                                          {note.userType === 1 && (
                                            <Chip
                                              label="Expert"
                                              size="small"
                                              color={note.userID === decodedToken?.userID ? 'default' : 'success'}
                                              sx={{
                                                height: '16px',
                                                ml: 0.75,
                                                '& .MuiChip-label': {
                                                  px: 0.75,
                                                  fontSize: '0.625rem',
                                                  fontWeight: 'medium',
                                                  textTransform: 'uppercase',
                                                },
                                              }}
                                            />
                                          )}
                                        </Box>
                                      )}
                                      {note.content}
                                    </Typography>
                                    <Box
                                      className="message-actions"
                                      sx={{
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                        display: note.userID === decodedToken?.userID ? 'flex' : 'none',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        position: 'absolute',
                                        right: 0,
                                        top: 0.5,
                                        zIndex: 2,
                                      }}
                                    >
                                      <Tooltip title="Edit" placement="left">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleStartEdit(note)}
                                          sx={{
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            opacity: 0.2,
                                            boxShadow: 1,
                                            width: 28,
                                            height: 28,
                                            '&:hover': {
                                              bgcolor: 'primary.main',
                                              boxShadow: 2,
                                              opacity: 1,
                                            },
                                          }}
                                        >
                                          <EditOutlinedIcon sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete" placement="left">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeleteClick(note)}
                                          sx={{
                                            bgcolor: 'error.main',
                                            color: 'white',
                                            opacity: 0.2,
                                            boxShadow: 1,
                                            width: 28,
                                            height: 28,
                                            '&:hover': {
                                              bgcolor: 'error.main',
                                              boxShadow: 2,
                                              opacity: 1,
                                            },
                                          }}
                                        >
                                          <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </>
                                )}
                              </Box>
                              {(isLastInSequence || timeDiffWithNext >= TWO_MINUTES) && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: '0.75rem',
                                    display: 'block',
                                    mt: 0.5,
                                    ml: 0.5,
                                  }}
                                >
                                  {formattedDates[note.submitDate]}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        {notes.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notes yet. Add the first note to start the conversation.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Delete Note</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Are you sure you want to delete this note? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error" sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

NotesSection.propTypes = {
  companyName: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  allReports: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({
      data: PropTypes.array,
    }),
  ]).isRequired,
  allReportsMutate: PropTypes.func.isRequired,
  decodedToken: PropTypes.shape({
    userID: PropTypes.number,
    userType: PropTypes.number,
  }).isRequired,
  isReportsLoading: PropTypes.bool,
};

export default NotesSection;
